import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Box, Group, Progress, ScrollArea, Stack, Text } from '@mantine/core';
import { t } from 'i18next';
import { FiCheck } from 'react-icons/fi';
import { usePluginProgress, usePluginRows } from '@morph-mapper/plugins';

/*
 * The per-plugin progress rows, shared by the setup's Loading Plugins step
 * and the header's re-evaluation progress.
 */

// ─── Progress bar helpers ────────────────────────────────────────────────────

type ProgressEntry = ReturnType<typeof usePluginProgress>[number];

const getPhaseLabel = (value: number): string => {
  // Plugins cap their own reporting at 85 — the last 15% is the pipeline
  // applying their results. Only 100 is genuinely Done; 85-99 means this
  // plugin has finished its part and is waiting on the pipeline.
  if (value >= 100) return 'Done';
  if (value >= 85) return 'Waiting for pipeline';
  if (value >= 76) return 'Merging';
  if (value >= 64) return 'Retrying';
  if (value >= 60) return 'Scoring';
  if (value >= 20) return 'Extracting';
  return 'Initializing';
};

export const getBarColor = (value: number): string => {
  if (value >= 100) return 'green';
  if (value >= 60) return 'teal';
  return 'blue';
};

const isWaitingPhase = (value: number) =>
  (value >= 20 && value < 56) || (value >= 64 && value < 76);

// ─── Per-entry progress row ──────────────────────────────────────────────────

const PluginProgressEntry = ({ entry }: { entry: ProgressEntry }) => {
  const [displayed, setDisplayed] = useState(0);
  const [elapsedS, setElapsedS] = useState(0);
  const [flashing, setFlashing] = useState(false);
  const [phase, setPhase] = useState(() => getPhaseLabel(entry.value));
  const rafRef = useRef<number | undefined>(undefined);
  const prevPhaseRef = useRef(phase);

  // Always-running 1s ticker — resets when phase changes
  useEffect(() => {
    const id = setInterval(() => setElapsedS((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Detect phase transitions and reset elapsed counter
  useEffect(() => {
    const newPhase = getPhaseLabel(entry.value);
    if (newPhase !== prevPhaseRef.current) {
      prevPhaseRef.current = newPhase;
      setPhase(newPhase);
      setElapsedS(0);
    }
  }, [entry.value]);

  // RAF animation — proportional catch-up with minimum floor; stops when converged
  useEffect(() => {
    const target = entry.value;
    let done = false;
    const tick = () => {
      setDisplayed((prev) => {
        const diff = target - prev;
        if (diff <= 0.01) {
          done = true;
          return target;
        }
        // proportional easing, floor of 0.04%/frame ≈ 2.4%/s at 60fps
        return Math.min(target, prev + Math.max(0.04, diff * 0.04));
      });
      if (!done) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = undefined;
        if (target >= 100) {
          setFlashing(true);
          setTimeout(() => setFlashing(false), 700);
        }
      }
    };
    if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    done = false;
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, [entry.value]);

  // extractionDone: plugin finished its work (≥85%). Pipeline may still be running (85→100).
  // fullyDone: pipeline delivered 100% — flash and stop all motion.
  const extractionDone = phase === 'Done';
  const isWaiting = isWaitingPhase(entry.value);
  const color = getBarColor(entry.value);
  const showElapsed = (phase === 'Extracting' || phase === 'Retrying') && elapsedS >= 3;

  return (
    <Box>
      <Group position="apart" mb={4}>
        <Group spacing={6}>
          <Text fz="xs" fw={600} c="dimmed" tt="uppercase" lts={0.5}>
            {entry.plugin}
          </Text>
          <Text fz="xs" c="dimmed">·</Text>
          <Text fz="xs" c={extractionDone ? 'green' : 'dimmed'} fw={extractionDone ? 600 : 400}>
            {phase}
          </Text>
        </Group>
        <Group spacing={6}>
          {showElapsed && (
            <Text fz="xs" c="dimmed">{elapsedS}s</Text>
          )}
          <Text fz="xs" fw={700} c={extractionDone ? 'green' : 'blue'}>
            {Math.round(displayed)}%
          </Text>
        </Group>
      </Group>

      <Box
        sx={{
          position: 'relative',
          borderRadius: 6,
          animation: flashing ? 'donePulse 0.7s ease-out' : undefined,
        }}
      >
        <Progress
          value={displayed}
          size="lg"
          radius="md"
          color={color}
          striped={isWaiting}
          animate={!extractionDone}
        />
        {!extractionDone && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${displayed}%`,
              bottom: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
              borderRadius: 'inherit',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: '-50%',
                width: '50%',
                bottom: 0,
                background:
                  'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)',
                animation: 'morphShimmer 1.8s ease-in-out infinite',
              }}
            />
          </Box>
        )}
      </Box>

      {entry.message && (
        <Text fz="xs" c="dimmed" mt={4}>
          {entry.message}
        </Text>
      )}
    </Box>
  );
};

// ─── Row for a plugin that does not report progress ──────────────────────────

/**
 * Plugins that never call progress() still get a row, so the view does not
 * silently omit plugins that are part of the run.
 *
 * The only signal available for them is `bootstrapped`, which means their
 * bootstrap hook resolved — not that their work is finished. Several of these
 * do their real work later in postFieldSuggestions, so showing "Done" here
 * claimed a completion that had not happened. The row reports loaded-or-not
 * and leaves the bar empty rather than inventing a percentage.
 */
const PluginStateRow = ({ plugin, ready }: { plugin: string; ready: boolean }) => (
  <Box>
    <Group position="apart" mb={2}>
      <Text fz="xs" fw={600} c="dimmed" tt="uppercase" lts={0.5}>
        {plugin}
      </Text>
      <Group spacing={4}>
        {ready && <FiCheck size={13} color="var(--mantine-color-green-6, #40c057)" />}
        <Text fz="xs" c="dimmed" fw={400}>
          {ready ? t('common.pluginReady') : t('common.pluginWaiting')}
        </Text>
      </Group>
    </Group>
    <Text fz="xs" c="dimmed">
      {ready
        ? t('description.pluginRunsPerField')
        : t('description.pluginNotLoaded')}
    </Text>
  </Box>
);

export type PluginRow = ReturnType<typeof usePluginRows>[number];

/**
 * Whether a plugin has finished its own part: 85% or more for one that
 * reports progress (the rest is the pipeline), loaded for one that doesn't.
 */
export const isRowReady = (row: PluginRow) =>
  row.reports ? row.value >= 85 : row.bootstrapped;

/**
 * The average progress of the plugins that report it. Undefined when none
 * do: the others only say whether they loaded, so a percentage would be
 * made up.
 */
export const overallProgress = (rows: PluginRow[]): number | undefined => {
  const reporting = rows.filter((row) => row.reports);
  if (reporting.length === 0) return undefined;
  const total = reporting.reduce((sum, row) => sum + Math.min(100, row.value), 0);
  return Math.round(total / reporting.length);
};

// ─── The list ────────────────────────────────────────────────────────────────

/** Adds the keyframes the rows animate with to the page, once. */
const useProgressKeyframes = () => {
  useEffect(() => {
    const id = 'morph-progress-keyframes';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      @keyframes morphShimmer {
        0%   { left: -50%; }
        100% { left: 150%; }
      }
      @keyframes donePulse {
        0%, 100% { filter: brightness(1); }
        40%       { filter: brightness(1.5); }
      }
    `;
    document.head.appendChild(style);
  }, []);
};

// How tall a list gets before it scrolls, so a long list stays inside its
// dropdown instead of pushing the page down (setup) or running off the
// screen (header).
const SCROLL_BOX_MAX_HEIGHT = 'min(360px, 50vh)';

/**
 * The scroll box the plugin rows and the plugin logs sit in. Its scrollbar
 * shows as soon as the content doesn't fit.
 */
export const PluginScrollBox = ({ children }: { children: ReactNode }) => (
  <ScrollArea.Autosize
    sx={{ maxHeight: SCROLL_BOX_MAX_HEIGHT }}
    type="auto"
    offsetScrollbars
    // Required by Mantine 6's types for Autosize; they do nothing here.
    placeholder={undefined}
    onPointerEnterCapture={undefined}
    onPointerLeaveCapture={undefined}
  >
    {children}
  </ScrollArea.Autosize>
);

/**
 * A row per active plugin: a progress bar for the plugins that report
 * progress, a loaded-or-not state for the others, in a scroll box.
 */
export const PluginProgressRows = ({ rows }: { rows: PluginRow[] }) => {
  useProgressKeyframes();
  return (
    <PluginScrollBox>
      <Stack spacing={12}>
        {rows.map((row) =>
          row.reports ? (
            <PluginProgressEntry key={row.plugin} entry={row} />
          ) : (
            <PluginStateRow
              key={row.plugin}
              plugin={String(row.plugin)}
              ready={row.bootstrapped}
            />
          ),
        )}
      </Stack>
    </PluginScrollBox>
  );
};
